import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SystemArchitecture } from './SystemArchitecture';

export function HeroSection() {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const content = {
    en: {
      eyebrow: 'Software Engineering',
      headline: 'Engineering Intelligent Software',
      subtext: 'We design and build production-grade SaaS, AI and financial systems for startups and enterprises.',
      cta1: 'Start a Conversation',
      cta2: 'View Case Studies',
      trustTags: ['AI Systems', 'Financial Infrastructure', 'Production SaaS'],
      microTrust: 'Free architecture consultation • NDA available • 48h response'
    },
    tr: {
      eyebrow: 'Yazılım Mühendisliği',
      headline: 'Akıllı Yazılım Mühendisliği',
      subtext: 'Startup\'lar ve kurumsal şirketler için production-grade SaaS, yapay zeka ve finansal sistemler tasarlıyor ve geliştiriyoruz.',
      cta1: 'Görüşme Başlat',
      cta2: 'Referansları İncele',
      trustTags: ['AI Sistemleri', 'Finansal Altyapı', 'Production SaaS'],
      microTrust: 'Ücretsiz mimari danışmanlık • NDA mevcut • 48 saat yanıt'
    },
    de: {
      eyebrow: 'Software Engineering',
      headline: 'Engineering Intelligent Software',
      subtext: 'Wir entwerfen und entwickeln produktionsreife SaaS-, KI- und Finanzsysteme für Startups und Unternehmen.',
      cta1: 'Gespräch starten',
      cta2: 'Fallstudien ansehen',
      trustTags: ['KI-Systeme', 'Finanzinfrastruktur', 'Production SaaS'],
      microTrust: 'Kostenlose Architekturberatung • NDA verfügbar • 48h Antwort'
    }
  };

  const c = content[currentLanguage] || content.en;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[50vh] md:min-h-[60vh] lg:min-h-screen flex items-center overflow-hidden mb-28"
      style={{ background: 'var(--bg-base)' }}
    >
      {/* Subtle gradient background */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(15, 61, 62, 0.25), transparent)'
        }}
      />

      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: 'linear-gradient(rgba(230,237,243,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(230,237,243,0.08) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-40 pb-12 md:pt-32 md:pb-16 lg:pt-28 lg:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column */}
          <div>
            {/* Eyebrow */}
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-8"
              style={{ 
                background: 'rgba(15, 61, 62, 0.25)',
                borderColor: 'rgba(28, 124, 125, 0.2)'
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#1C7C7D]" />
              <span className="text-xs font-medium tracking-wide" style={{ color: '#1C7C7D' }}>
                {c.eyebrow}
              </span>
            </div>

            {/* Main Headline - Single line */}
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8"
              style={{ color: 'var(--text-primary)' }}
            >
              {c.headline}
            </h1>

            {/* Subtext */}
            <p 
              className="text-base md:text-lg leading-relaxed mb-10 max-w-xl"
              style={{ color: 'var(--text-muted)' }}
            >
              {c.subtext}
            </p>

            {/* CTAs - Primary: Case Studies, Secondary: Contact */}
            <div className="flex flex-wrap gap-4 mb-5">
              <button
                onClick={() => navigate('/case-studies')}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-medium text-sm transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #0F3D3E 0%, #1C7C7D 100%)',
                  color: '#F0F4F8',
                  border: '1px solid rgba(28, 124, 125, 0.4)',
                  boxShadow: '0 0 20px rgba(28, 124, 125, 0.15)'
                }}
              >
                {c.cta2}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </button>
              
              <button
                onClick={() => navigate('/contact')}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-medium text-sm transition-all duration-300 hover:bg-[rgba(255,255,255,0.05)]"
                style={{
                  color: 'var(--text-secondary)',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                {c.cta1}
              </button>
            </div>

            {/* Micro-trust line */}
            <p 
              className="text-xs"
              style={{ color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
            >
              {c.microTrust}
            </p>

            {/* Trust Tags */}
            <div className="flex flex-wrap gap-3 mt-8 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              {c.trustTags.map((tag, i) => (
                <span 
                  key={i}
                  className="text-xs px-3 py-1.5 rounded-full"
                  style={{ 
                    background: 'rgba(28, 124, 125, 0.1)',
                    color: 'rgba(28, 124, 125, 0.8)',
                    border: '1px solid rgba(28, 124, 125, 0.15)'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Right Column - System Architecture */}
          <div className="hidden lg:block relative">
            <div 
              className="relative rounded-xl overflow-hidden"
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-default)'
              }}
            >
              {/* Terminal Header */}
              <div 
                className="flex items-center gap-2 px-5 py-3 border-b"
                style={{ borderColor: 'var(--border-subtle)' }}
              >
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
                </div>
                <span 
                  className="ml-3 text-xs tracking-wide"
                  style={{ color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                >
                  system_architecture.tsx
                </span>
              </div>

              {/* Visualization */}
              <div className="p-6">
                <SystemArchitecture />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
